import { z } from 'zod';

// Email validation with case-insensitive handling
const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address')
  .max(255, 'Email is too long')
  .transform((email) => email.toLowerCase().trim());

// Password validation with strength requirements
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .regex(/^(?=.*[a-z])/, 'Password must contain at least one lowercase letter')
  .regex(/^(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
  .regex(/^(?=.*\d)/, 'Password must contain at least one number');

// First name validation
const firstNameSchema = z
  .string()
  .min(1, 'First name is required')
  .max(50, 'First name is too long')
  .regex(/^[a-zA-Z\s'-]+$/, 'First name can only contain letters, spaces, hyphens, and apostrophes');

// Last name validation
const lastNameSchema = z
  .string()
  .min(1, 'Last name is required')
  .max(50, 'Last name is too long')
  .regex(/^[a-zA-Z\s'-]+$/, 'Last name can only contain letters, spaces, hyphens, and apostrophes');

// Check email schema
export const checkEmailSchema = z.object({
  email: emailSchema,
});

// Login schema
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

// Register schema
export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// Type exports for TypeScript
export type CheckEmailRequest = z.infer<typeof checkEmailSchema>;
export type LoginRequest = z.infer<typeof loginSchema>;
export type RegisterRequest = z.infer<typeof registerSchema>;

// Simple validation functions for client-side use
export const validateEmail = (email: string): string | null => {
  if (!email.trim()) {
    return 'Email is required';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  if (email.length > 255) {
    return 'Email is too long';
  }
  
  return null;
};

export const validatePassword = (password: string): string | null => {
  if (!password) {
    return 'Password is required';
  }
  
  if (password.length < 8) {
    return 'Password must be at least 8 characters';
  }
  
  if (password.length > 128) {
    return 'Password is too long';
  }
  
  if (!/(?=.*[a-z])/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  
  if (!/(?=.*[A-Z])/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  
  if (!/(?=.*\d)/.test(password)) {
    return 'Password must contain at least one number';
  }
  
  return null;
};

export const validateFirstName = (firstName: string): string | null => {
  if (!firstName.trim()) {
    return 'First name is required';
  }
  
  if (firstName.length > 50) {
    return 'First name is too long';
  }
  
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(firstName)) {
    return 'First name can only contain letters, spaces, hyphens, and apostrophes';
  }
  
  return null;
};

export const validateLastName = (lastName: string): string | null => {
  if (!lastName.trim()) {
    return 'Last name is required';
  }
  
  if (lastName.length > 50) {
    return 'Last name is too long';
  }
  
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  if (!nameRegex.test(lastName)) {
    return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
  }
  
  return null;
};

export const validatePasswordConfirmation = (confirmPassword: string, password?: string): string | null => {
  if (!confirmPassword) {
    return 'Please confirm your password';
  }
  
  if (!password) {
    return 'Password is required';
  }
  
  if (password !== confirmPassword) {
    return "Passwords don't match";
  }
  
  return null;
};
